// app/src/hooks/useActiveForecast.js
import { useSession } from "./useSession.js";
import { useRunStore } from "../store/runStore.js";
import { selectLegacyActive } from "../lib/forecast/selectors.js";

// `selectLegacyActive` caches per Run (`_forecastCache` in lib/forecast/selectors.ts), but that
// entry is keyed on the run's subtree and display day, not on the flag: callers reading a
// projectable run under alternating flags evict each other's entry on every call, and zustand
// compares a selector's result with `Object.is`, so the fresh object would re-render on every
// store change and trip the "selector returned a different value" guard. The store replaces its
// state object on every dispatch, so a WeakMap keyed on that object holds ONE object per
// `runState × flag`: the identity `AppInner`'s `useMemo` gave, now shared by every caller, so
// effects keyed on `active` fire exactly as before.
const cache = new WeakMap();

/** `selectLegacyActive(state, flag)`, computed once per `(state, flag)` and reused by reference. */
export function cachedLegacyActive(state, flag) {
  let byFlag = cache.get(state);
  if (!byFlag) {
    byFlag = new Map();
    cache.set(state, byFlag);
  }
  if (!byFlag.has(flag)) byFlag.set(flag, selectLegacyActive(state, flag));
  return byFlag.get(flag);
}

/**
 * The active forecast (`LegacyForecast`, or null without an active run), projected for the
 * session's `forecastLiveProgressEnabled` flag. Same reference for every caller on the same store
 * state and flag; a new one only when a dispatch changes the state.
 */
export function useActiveForecast() {
  const { forecastLiveProgressEnabled } = useSession();
  return useRunStore((s) => cachedLegacyActive(s, forecastLiveProgressEnabled));
}
